import {
    STREAM_QUOTE,
    SEARCH_QUOTE_AGGREGATED_DATA,
    STREAM_PART_PARTS,
    SEARCH_VENDORS_PARTS,
    SEARCH_PART_MINIMUMS,
    LIVE_SEARCH,
    SEARCH_PART_BANNER_ALERT,
    UPDATE_PART_BANNER_ALERT,
    SEARCH_SESSION_BY_SEARCH_ACTION_ID,
    SEARCH_ACTION_BY_SEARCH_ACTION_ID,
    SEARCHACTION_FINISH,
    SEARCHACTION_CUSTOMER_REFERENCE
} from '@/data/url'
import config from '@/services/config'
import apiClient from '@/services/apiClient'
import apiStreamClient from '@/services/apiStreamClient'
import { unwrapApiData } from '@/services/apiEnvelope'

const fetchVendorsParts = async (partNumberArray, searchActionId, substitutes) => {
    let loading = true
    let displayData = true
    let searching = true
    let newSearch = true

    try {
        const response = await apiStreamClient.fetch(SEARCH_VENDORS_PARTS, {
            method: 'POST',
            abortKey: 'stream:vendor',
            headers: {
                Authorization: `Bearer ${config.sessionToken}`,
            },
            body: {
                part_numbers: partNumberArray,
                search_action_id: searchActionId.toString(),
                vendors: [],
                master_substitutes: substitutes,
            },
        })

        if (!response.body) {
            console.error('Response body is empty')
            return
        }

        const reader = response.body.getReader()

        loading = false

        return {
            loading,
            displayData,
            searching,
            newSearch,
            reader,
        }
    } catch (error) {
        if (error.name === 'AbortError') {
            return { aborted: true }
        }
        console.error('Error:', error)
    }
}

const searchQuoteAggregatedData = async (
    partsArray,
    quantityArray,
    searchActionId,
    searchType,
    searchCustomer,
    customerRef,
    isNsn,
    forceSearch = false,
    master_substitutes
) => {
    let loading = true
    let displayData = true
    let searching = true
    let newSearch = true

    let jsonPost = {
        part_numbers: partsArray,
        quantity: quantityArray,
        customer: searchCustomer,
        quote_type: searchType,
        customer_ref: customerRef,
        is_nsn: isNsn,
        force_search: forceSearch,
        search_action_id: searchActionId || null,
        master_substitutes: master_substitutes,
    }

    try {
        const response = await apiClient.post(SEARCH_QUOTE_AGGREGATED_DATA, jsonPost)

        const results = unwrapApiData(response, {})

        loading = false

        return {
            loading,
            displayData,
            searching,
            newSearch,
            results,
        }
    } catch (error) {
        console.error('Error:', error)
    }
}

const searchStreamVendors = async (
    partsText,
    quantityArray,
    searchActionId,
    searchType,
    searchCustomer,
    customerRef,
    isNsn,
    forceSearch = false
) => {
    let loading = true
    let displayData = true
    let searching = true
    let newSearch = true

    let jsonPost = {
        part_number: partsText,
        quantity_list: quantityArray,
        customer: searchCustomer,
        quote_type: searchType,
        customer_ref: customerRef,
        is_nsn: isNsn,
        force_search: forceSearch,
        search_action_id: searchActionId,
    }

    try {
        const response = await apiClient.post(STREAM_PART_PARTS, jsonPost)

        const results = unwrapApiData(response, [])

        loading = false

        return {
            loading,
            displayData,
            searching,
            newSearch,
            results,
        }
    } catch (error) {
        console.error('Error:', error)
    }
}

const streamQuote = async (searchActionId) => {
    let loading = true
    let displayData = true
    let searching = true
    let newSearch = true

    try {
        const response = await apiStreamClient.fetch(STREAM_QUOTE, {
            method: 'POST',
            body: {
                search_action_id: searchActionId,
            },
        })

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        // const data = await response.text();

        const results = response.body

        loading = false

        return {
            loading,
            displayData,
            searching,
            newSearch,
            results,
        }
    } catch (error) {
        console.error('Error:', error)
        return null
    }
}

const streamLiveSearch = async (partNumberArray, searchActionId, master_substitutes, signal) => {

    const payload = {
        part_numbers: partNumberArray,
        search_action_id: searchActionId.toString(),
        master_substitutes: master_substitutes
    }

    try {
        const response = await apiStreamClient.fetch(LIVE_SEARCH, {
            method: 'POST',
            signal,
            body: payload,
        })

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        return {
            reader: response.body.getReader(),
        }
    } catch (error) {
        if (error.name === 'AbortError') {
            return { aborted: true }
        }
        console.error('Error:', error)
        return []
    }
}

const fetchLiveSearchStatus = async (searchActionId, flowId, offset, signal) => {
    const response = await apiClient.get(`${LIVE_SEARCH}${searchActionId}/status/`, {
        params: { parent_flow_run_id: flowId, offset },
        signal,
        timeout: 20000,
    })
    return unwrapApiData(response, null)
}

const fetchPartsMinimums = async (partNumbers, customer) => {
    const payload = {
        part_numbers: partNumbers,
        customer: customer || null,
    }

    try {
        const response = await apiClient.post(SEARCH_PART_MINIMUMS, payload)
        return unwrapApiData(response, {})
    } catch (error) {
        console.error('Error:', error)
    }
}

const streamController = async (flowID) => {
    try {
        const response = await apiStreamClient.fetch(
            `/parts/live-search-control/?parent_flow_run_id=${flowID}`,
            {
                method: 'GET',
            }
        )

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const responseReader = response.body.getReader()
        return responseReader
    } catch (error) {
        // console.error('Error:', error);
        return []
    }
}

const updateSearch = async (searchActionId, partsText) => {
    let jsonPost = {
        search_action_id: searchActionId,
        part_number: partsText,
    }

    try {
        const response = await apiClient.post('/api/search/update-part-list', jsonPost)

        return unwrapApiData(response, {})
    } catch (error) {
        console.error('Error:', error)
    }
}

const getFuzzyPartsMatches = async (partsText) => {
    const payload = {
        part_number: partsText,
    }

    try {
        const response = await apiClient.post('/api/search/fuzzy_parts_matches', payload)

        return unwrapApiData(response, {})
    } catch (error) {
        console.error('Error:', error)
    }
}

const getAlertBanner = async (partsText) => {
    let jsonPost = {
        part_number: partsText,
    }

    try {
        const response = await apiClient.post(SEARCH_PART_BANNER_ALERT, jsonPost)

        return unwrapApiData(response, [])
    } catch (error) {
        console.error('Error:', error)
    }
}

const updateBannerStatus = async (partsText) => {
    let jsonPost = {
        part_number: partsText,
    }

    try {
        const response = await apiClient.post(UPDATE_PART_BANNER_ALERT, jsonPost)

        return unwrapApiData(response, {})
    } catch (error) {
        console.error('Error:', error)
    }
}

const markSearchActionFinished = async (searchActionId) => {
    const payload = {
        search_action_id: searchActionId,
        quote_finished: true,
    }

    try {
        const response = await apiClient.patch(SEARCHACTION_FINISH, payload)
        return unwrapApiData(response, {})
    } catch (error) {
        console.error('Error marking search action as finished:', error)
    }
}

const fetchSessionData = async (searchActionId) => {
    try {
        const response = await apiClient.get(
            `${SEARCH_SESSION_BY_SEARCH_ACTION_ID}/${searchActionId}`
        )
        return unwrapApiData(response, [])
    } catch (error) {
        console.error('Error marking search action as finished:', error)
    }
}

const fetchSearchActionData = async (searchActionId) => {
    try {
        const response = await apiClient.get(
            `${SEARCH_ACTION_BY_SEARCH_ACTION_ID}/${searchActionId}`
        )
        return unwrapApiData(response, null)
    } catch (error) {
        console.error('Error marking search action as finished:', error)
    }
}

const check_search_action_exists_for_customer_reference = async (customerReference) => {
    try {
        const response = await apiClient.get(
            `${SEARCHACTION_CUSTOMER_REFERENCE}/${customerReference}`
        )
        return unwrapApiData(response, null) !== null
    } catch (error) {
        console.error('Error checking if search action exists for customer reference:', error)
    }
}

export default {
    fetchVendorsParts,
    streamQuote,
    searchQuoteAggregatedData,
    searchStreamVendors,
    streamLiveSearch,
    fetchLiveSearchStatus,
    streamController,
    updateSearch,
    getFuzzyPartsMatches,
    getAlertBanner,
    updateBannerStatus,
    markSearchActionFinished,
    fetchSessionData,
    fetchSearchActionData,
    fetchPartsMinimums,
    check_search_action_exists_for_customer_reference
}
